import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { RegionService } from '../../core/region.service';
import { ERROR_CODES, ErrCode } from '../models';
import { TurnstileRenderOpts, TurnstileSdk } from './turnstile.types';

// Maximum time to wait for the Cloudflare script to expose window.turnstile.
const WHEN_READY_TIMEOUT_MS = 10000;
const POLL_INTERVAL_MS = 100;

@Injectable({
  providedIn: 'root',
})
export class TurnstileService {
  private readyPromise?: Promise<TurnstileSdk>;

  constructor(private regionService: RegionService) {}

  public isEnabled(): boolean {
    return Boolean(environment.turnstileEnabled) && this.getSiteKey().length > 0;
  }

  public getSiteKey(): string {
    const region = this.regionService.detectRegion();
    return environment.turnstileSiteKeyRegionMap[region] || '';
  }

  /**
   * Returns true when the submit button / form should be blocked waiting for a Turnstile token.
   * In production, a widget load failure is treated as a graceful fallback (verification becomes
   * effectively a no-op for this session, backend decides via existing error handling).
   */
  public isGated(turnstileToken: string | null | undefined, loadError: boolean): boolean {
    if (!this.isEnabled()) {
      return false;
    }
    if (turnstileToken) {
      return false;
    }
    if (loadError && environment.production) {
      return false;
    }
    return true;
  }

  public whenReady(): Promise<TurnstileSdk> {
    if (this.readyPromise) {
      return this.readyPromise;
    }
    this.readyPromise = new Promise<TurnstileSdk>((resolve, reject) => {
      const start = Date.now();
      const poll = (): void => {
        if (window.turnstile) {
          resolve(window.turnstile);
          return;
        }
        if (Date.now() - start >= WHEN_READY_TIMEOUT_MS) {
          this.readyPromise = undefined;
          reject(new ErrCode(ERROR_CODES.API_EMPTY_RESULT, 'Turnstile failed to load'));
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      };
      poll();
    });
    return this.readyPromise;
  }

  public async render(host: HTMLElement, opts: TurnstileRenderOpts): Promise<string> {
    const sdk = await this.whenReady();
    return sdk.render(host, opts);
  }

  public reset(widgetId: string): void {
    if (window.turnstile) {
      window.turnstile.reset(widgetId);
    }
  }

  public remove(widgetId: string): void {
    if (window.turnstile) {
      window.turnstile.remove(widgetId);
    }
  }
}
